/* eslint-disable no-param-reassign */
import { createAction, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ViewForSiderFieldsFragment, ViewGroupFieldsFragment } from '@salescore/client-api'
import {
  addQueryParameterToUrl,
  getQueryParameter,
  logger,
  removeQueryParameterFromUrl,
} from '@salescore/frontend-common'
import { Array, Brand, pipe, Record } from 'effect'
import { isNotUndefined } from 'effect/Predicate'
import { z } from 'zod'

/**
 * View Mount ID (vmid)
 */
export type Vmid = string & Brand.Brand<'Vmid'>

export const Vmid = Brand.make<Vmid>((value) => z.uuidv4().safeParse(value).success || `Invalid Vmid: ${value}`)

function loadFromLocalStorage<T>(key: string, schema: z.ZodType<T>): T | undefined {
  const item = globalThis.localStorage.getItem(key)
  if (item === null) {
    return undefined
  }
  try {
    return schema.parse(JSON.parse(item))
  } catch {
    logger.warn('WARN', `Failed to parse localStorage item for key "${key}".`)
  }
}

interface ViewParameters {
  pageSize: number | undefined
}

/**
 * ViewComponentのロード時に保存されたパラメーターを読み込む
 */
export const loadViewParameters = (viewId: string): ViewParameters => {
  const pageSize = loadFromLocalStorage(`view/${viewId}/pageSize`, z.number())

  return {
    pageSize,
  }
}

export const hydrateViewsSlice = createAction('views/hydrateViewsSlice', () => {
  const shareIdUrlQueryParameter = getQueryParameter('shareId')
  const pickedViewIdFromQueryParameter = getQueryParameter('viewId')
  const pickedViewIdFromLocalStorage = loadFromLocalStorage('views/pickedViewId', z.string())

  if (pickedViewIdFromQueryParameter === null && pickedViewIdFromLocalStorage !== undefined) {
    addQueryParameterToUrl('viewId', pickedViewIdFromLocalStorage)
  }

  const pickedViewId = pickedViewIdFromQueryParameter ?? pickedViewIdFromLocalStorage
  const pickedViewIds = pipe(
    loadFromLocalStorage('views/pickedViewIds', z.string().array()) ?? [],
    Array.append(pickedViewId),
    Array.filter(isNotUndefined),
    Array.dedupe,
  )

  return {
    payload: {
      pickedViewId,
      pickedViewIds,
      shareId: shareIdUrlQueryParameter ?? undefined,
    },
  }
})

export const setPickedViewIdAndPersist = createAction(
  'views/setPickedViewIdAndPersist',
  (viewId: string | undefined) => {
    localStorage.setItem('views/pickedViewId', JSON.stringify(viewId ?? ''))
    if (viewId !== undefined) {
      addQueryParameterToUrl('viewId', viewId)
    }
    removeQueryParameterFromUrl(`searchQuery`)
    return {
      payload: viewId,
    }
  },
)

export const setPickedViewIdsAndPersist = createAction('views/setPickedViewIdsAndPersist', (viewIds: string[]) => {
  localStorage.setItem('views/pickedViewIds', JSON.stringify(viewIds))
  return {
    payload: viewIds,
  }
})

export const setPageSizeAndPersist = createAction(
  'view/setPageSizeAndPersist',
  (payload: { viewId: string; pageSize: number }) => {
    const { viewId, pageSize } = payload
    localStorage.setItem(`view/${viewId}/pageSize`, JSON.stringify(pageSize))
    return {
      payload,
    }
  },
)

export type HighlightConfigKey = '' | '1 day' | '2 day' | '3 day' | '1 week'

export interface CursorPosition {
  columnIndex: number
  rowIndex: number
  innerRowIndex: number
}

// RSheetsCursor 型を CursorEntity として複製（依存方向を守るため feature-view -> redux の依存を作らない）
export interface CursorEntity {
  main: CursorPosition
  expand?: CursorPosition
  editing?: {
    isEditing?: boolean
    initialPressedKey?: string
  }
  contextMenuPosition?: {
    x: number
    y: number
  }
  copying?: {
    main: CursorPosition
    expand?: CursorPosition
    visible: boolean
  }
}

export interface ViewState {
  viewId: string // データベースに格納されているビューの ID で、リロード前後で変化しない cf. vmid
  isSavingSettings: boolean
  isPasting: boolean
  pageSize: number
  highlightConfigKey: HighlightConfigKey
  cursor: CursorEntity | undefined
  longTextViewerOpenKey: string | undefined
}

const defaultViewState = {
  isSavingSettings: false,
  isPasting: false,
  pageSize: 100,
  highlightConfigKey: '' as const,
  cursor: undefined,
  longTextViewerOpenKey: undefined,
}

export interface ViewsState {
  shareId: string | undefined
  childViewIds: Record<string, string> // viewId -> childViewId
  siderViews: Array<Omit<ViewForSiderFieldsFragment, '__typename'>>
  viewGroups: ViewGroupFieldsFragment[]
  pickedViewId?: string
  pickedViewIds: string[]
  durationMetrics: Record<string, DurationMetrics>
  viewStates: Record<Vmid, ViewState> // Vmid -> ViewState
}

const initialState: ViewsState = {
  shareId: undefined,
  childViewIds: {},
  siderViews: [],
  viewGroups: [],
  pickedViewId: undefined,
  pickedViewIds: [],
  durationMetrics: {},
  viewStates: {},
}

export const viewsSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    setShareId: (state, action: PayloadAction<string | undefined>) => {
      state.shareId = action.payload
    },
    setChildViewId: (state, action: PayloadAction<{ viewId: string; childViewId: string }>) => {
      const { viewId, childViewId } = action.payload
      state.childViewIds[viewId] = childViewId
    },
    deleteDescendantViewIds: (state, action: PayloadAction<string>) => {
      const rootViewId = action.payload
      for (
        let viewId = rootViewId, childViewId = state.childViewIds[viewId];
        childViewId !== undefined;
        viewId = childViewId, childViewId = state.childViewIds[viewId]
      ) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- 全く問題ない
        delete state.childViewIds[viewId]
      }
    },
    setSiderViews: (state, action: PayloadAction<Array<Omit<ViewForSiderFieldsFragment, '__typename'>>>) => {
      state.siderViews = action.payload
    },
    upsertSiderView: (state, { payload: view }: PayloadAction<Omit<ViewForSiderFieldsFragment, '__typename'>>) => {
      const index = state.siderViews.findIndex((v) => v.id === view.id)
      if (index === -1) {
        state.siderViews = [...state.siderViews, view]
        return
      }
      state.siderViews[index] = view
    },
    deleteSiderView: (state, { payload: viewId }: PayloadAction<string>) => {
      state.siderViews = state.siderViews.filter((view) => view.id !== viewId)
    },
    setViewGroups: (state, action: PayloadAction<ViewGroupFieldsFragment[]>) => {
      state.viewGroups = action.payload
    },
    setPickedViewId: (state, action: PayloadAction<string | undefined>) => {
      state.pickedViewId = action.payload
    },
    setPickedViewIds: (state, action: PayloadAction<string[]>) => {
      state.pickedViewIds = action.payload
    },
    setDurationMetrics: (state, action: PayloadAction<DurationMetrics>) => {
      const metrics = action.payload
      state.durationMetrics[metrics.viewId] = metrics
    },
    deleteDurationMetrics: (state, action: PayloadAction<string>) => {
      const viewId = action.payload
      const metrics = state.durationMetrics[viewId]
      if (metrics === undefined) {
        logger.warn('WARN', `No duration metrics found for viewId: ${viewId}`)
      } else {
        state.durationMetrics = Object.fromEntries(
          Object.entries(state.durationMetrics).filter(([key]) => key !== viewId),
        )
      }
    },
    initializeViewState: (state, action: PayloadAction<Partial<ViewState> & { viewId: string; vmid: Vmid }>) => {
      const { vmid, ...overridingViewState } = action.payload
      state.viewStates[vmid] = {
        ...defaultViewState,
        ...overridingViewState,
      }
    },
    removeViewState: (state, action: PayloadAction<Vmid>) => {
      const vmid = action.payload
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- 全く問題ない
      delete state.viewStates[vmid]
    },
    setIsSavingSettings: (state, action: PayloadAction<{ vmid: Vmid; value: boolean }>) => {
      const { vmid, value } = action.payload
      if (state.viewStates[vmid] === undefined) {
        return
      }
      state.viewStates[vmid].isSavingSettings = value
    },
    setIsPasting: (state, action: PayloadAction<{ vmid: Vmid; value: boolean }>) => {
      const { vmid, value } = action.payload
      if (state.viewStates[vmid] === undefined) {
        return
      }
      state.viewStates[vmid].isPasting = value
    },
    setHighlightConfigKey: (state, action: PayloadAction<{ vmid: Vmid; value: HighlightConfigKey }>) => {
      const { vmid, value } = action.payload
      if (state.viewStates[vmid] === undefined) {
        return
      }
      state.viewStates[vmid].highlightConfigKey = value
    },
    setCursor: (state, action: PayloadAction<{ vmid: Vmid; cursor: CursorEntity | undefined }>) => {
      const { vmid, cursor } = action.payload
      const viewState = state.viewStates[vmid]
      if (viewState === undefined) {
        return
      }
      viewState.cursor = cursor
    },
    openLongTextViewer: (state, action: PayloadAction<{ vmid: Vmid; cellKey: string }>) => {
      const { vmid, cellKey } = action.payload
      const viewState = state.viewStates[vmid]
      if (viewState === undefined) {
        return
      }
      viewState.longTextViewerOpenKey = cellKey
    },
    // cellKey が現在の open key と一致する場合のみ close する。
    // race（A の cleanup が B の open 直後に走るケース）を no-op にするため。
    closeLongTextViewer: (state, action: PayloadAction<{ vmid: Vmid; cellKey: string }>) => {
      const { vmid, cellKey } = action.payload
      const viewState = state.viewStates[vmid]
      if (viewState === undefined) {
        return
      }
      if (viewState.longTextViewerOpenKey === cellKey) {
        viewState.longTextViewerOpenKey = undefined
      }
    },
    // 開いている viewer が何であれ閉じる。列リサイズ等、cellKey を保持しない上流から呼ぶ用途。
    closeAnyLongTextViewer: (state, action: PayloadAction<{ vmid: Vmid }>) => {
      const { vmid } = action.payload
      const viewState = state.viewStates[vmid]
      if (viewState === undefined) {
        return
      }
      viewState.longTextViewerOpenKey = undefined
    },
  },
  selectors: {
    selectShareId: (state) => state.shareId,
    selectChildViews: (state) => state.childViewIds,
    selectSiderViews: (state) => state.siderViews,
    selectViewGroups: (state) => state.viewGroups,
    selectPickedViewId: (state) => state.pickedViewId,
    selectPickedViewIds: (state) => state.pickedViewIds,
    selectDurationMetrics: (state, viewId: string | undefined) =>
      viewId === undefined ? undefined : state.durationMetrics[viewId],
    selectViewStates: (state) => state.viewStates,
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateViewsSlice, (state, action) => {
      state.pickedViewIds = action.payload.pickedViewIds
      state.pickedViewId = action.payload.pickedViewId
      state.shareId = action.payload.shareId
    })
    builder.addCase(setPickedViewIdAndPersist, (state, action) => {
      state.pickedViewId = action.payload
    })
    builder.addCase(setPickedViewIdsAndPersist, (state, action) => {
      state.pickedViewIds = action.payload
    })
    builder.addCase(setPageSizeAndPersist, (state, action) => {
      const { viewId, pageSize } = action.payload
      pipe(
        state.viewStates,
        Record.values,
        Array.filter((vs) => vs.viewId === viewId),
        Array.forEach((vs) => {
          vs.pageSize = pageSize
        }),
      )
    })
  },
})

export const {
  selectShareId,
  selectChildViews,
  selectSiderViews,
  selectViewGroups,
  selectPickedViewId,
  selectPickedViewIds,
  selectDurationMetrics,
  selectViewStates,
} = viewsSlice.selectors
export const {
  setShareId,
  setChildViewId,
  deleteDescendantViewIds,
  setSiderViews,
  upsertSiderView,
  deleteSiderView,
  setViewGroups,
  setDurationMetrics,
  deleteDurationMetrics,
  initializeViewState,
  removeViewState,
  setIsSavingSettings,
  setIsPasting,
  setHighlightConfigKey,
  setCursor,
  openLongTextViewer,
  closeLongTextViewer,
  closeAnyLongTextViewer,
} = viewsSlice.actions
export default viewsSlice.reducer

const selectParentViewIds = createSelector([selectChildViews], (childViews) =>
  Object.entries(childViews).reduce<Record<string, string>>((accumulator, [viewId, childViewId]) => {
    accumulator[childViewId] = viewId
    return accumulator
  }, {}),
)

export const selectAncestorViewIds = createSelector(
  [selectParentViewIds, (_, viewId: string) => viewId],
  (parentViewIds, rootViewId) => {
    const ancestorViewIds: string[] = []
    for (
      let viewId: string | undefined = parentViewIds[rootViewId];
      viewId !== undefined;
      viewId = parentViewIds[viewId]
    ) {
      ancestorViewIds.push(viewId)
    }
    return ancestorViewIds
  },
)

export const selectPredictedDuration = createSelector([selectDurationMetrics], (durationMetrics) => {
  if (durationMetrics === undefined) {
    return
  }
  return durationMetrics.isRefetchCacheUsed
    ? durationMetrics.cachedPredictedDuration
    : durationMetrics.noCachedPredictedDuration
})

export const selectViewState = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid],
)

export const selectViewStateViewId = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid]?.viewId,
)

export const selectViewStatePageSize = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid]?.pageSize,
)

export const selectViewStateIsSavingSettings = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid]?.isSavingSettings,
)

export const selectViewStateIsPasting = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid]?.isPasting,
)

export const selectViewStateHighlightConfigKey = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid]?.highlightConfigKey,
)

export const selectCursor = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid]?.cursor,
)

export const selectLongTextViewerOpenKey = createSelector(
  [selectViewStates, (_, vmid: Vmid) => vmid],
  (viewStates, vmid) => viewStates[vmid]?.longTextViewerOpenKey,
)

export interface DurationMetrics {
  viewId: string
  trigger: 'tabChanged' | 'drillDownModalOpened' | 'kpiPreviewUpdated'
  startAt: number
  isRefetchCacheUsed: boolean
  cachedPredictedDuration?: number
  noCachedPredictedDuration?: number
}
